import React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ontLogo from '../ont_logo.svg';

// Wait for enough active upload evidence before showing an ETA so the label
// does not flicker or swing wildly at the start of each barcode upload.
const MIN_UPLOAD_ELAPSED_SECONDS_FOR_ETA = 1;
const MIN_UPLOADED_BYTES_FOR_ETA = 1024 * 1024;

const TIME_BUCKETS = [
  [5, '~5 seconds'],
  [15, '~15 seconds'],
  [25, '~20 seconds'],
  [35, '~30 seconds'],
  [50, '~45 seconds'],
];

function getBucketLabel(seconds) {
  if (seconds >= 3600) {
    const hours = Math.ceil(seconds / 3600);
    return `~${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  if (seconds > 60) {
    const minutes = Math.ceil(seconds / 60);
    return `~${minutes} minutes`;
  }

  for (const [threshold, label] of TIME_BUCKETS) {
    if (seconds <= threshold) {
      return label;
    }
  }

  return '~1 minute';
}

function calculateTimeLabel(uploadEstimate) {
  const uploadedBytes = uploadEstimate?.uploadedBytes || 0;
  const totalBytes = uploadEstimate?.totalBytes || 0;
  if (!uploadEstimate?.startedAt || totalBytes <= 0 || uploadedBytes <= 0) {
    return null;
  }

  const elapsed = (uploadEstimate.uploadElapsedMs || 0) / 1000;
  if (elapsed < MIN_UPLOAD_ELAPSED_SECONDS_FOR_ETA || uploadedBytes < MIN_UPLOADED_BYTES_FOR_ETA) {
    return null;
  }

  const bytesPerSecond = uploadedBytes / elapsed;
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) {
    return null;
  }

  const remainingBytes = Math.max(0, totalBytes - uploadedBytes);
  if (remainingBytes <= 0) {
    return null;
  }

  const rawEstimate = remainingBytes / bytesPerSecond;
  const clampedEstimate = Math.max(5, rawEstimate);

  return getBucketLabel(clampedEstimate);
}

function MinKNOWUploadProgress({ open, progress, status, uploadEstimate }) {
  const timeLabel = calculateTimeLabel(uploadEstimate);

  return (
    <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogTitle>Uploading from Gourami Output Directory</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
            <img src={ontLogo} alt="ONT" style={{ height: 48 }} />
            <LinearProgress
              variant="indeterminate"
              color="secondary"
              sx={{ width: 96, height: 4, borderRadius: 2 }}
            />
            <img src="/ceph_logo.png" alt="Cepheid" style={{ height: 48 }} />
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 2, fontStyle: 'italic' }}
          >
            Uploading may take a few minutes for large datasets. Please do not close this window.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {progress >= 100 ? 'Upload complete' : `${Math.round(progress)}% complete`}
            </Typography>
            {timeLabel && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>
                Current upload time remaining: {timeLabel}
              </Typography>
            )}
          </Box>
          <Typography variant="body1">
            {status?.label ? (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {status.label}:
                </Box>
                {' '}{status.detail}
              </>
            ) : (
              status?.detail
            )}
          </Typography>
        </DialogContent>
      </Dialog>
    </Backdrop>
  );
}

export default MinKNOWUploadProgress;
